import { useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';
import { setTimezone, dayjs, Dayjs } from '@esg/shared';
import localesArgument from '@esg/shared/json/localesArgument.json';
import { currentLocaleState } from '../state/currentLocale';

const currencyDefault = 'USD';
const localesDefault: string[] = ['en-US'];

export const useLocalesFormat = () => {
    const [currentLocale] = useAtom(currentLocaleState);

    const locale = localesArgument.find((c) => c.country == currentLocale?.country)?.locales?.replace(/\s/g, '').split(',') || localesDefault;
    const currency = currentLocale?.currency;
    const timezone = currentLocale?.timezone;
    const defaultFormat = currentLocale?.format;

    useEffect(() => {
        setTimezone(timezone);
    }, [timezone]);

    const formatDate = useCallback((date: string | Dayjs, customFormat = defaultFormat) => {
        const dateTz = dayjs(date);

        return dateTz.format(customFormat);
    }, [defaultFormat, timezone]);

    const formatTime = useCallback((date: string | Dayjs | undefined) => {
        if (date === undefined) {
            return '-';
        }

        const dateTz = dayjs(date);
        return dateTz.format(`${defaultFormat} HH:mm`);
    }, [defaultFormat, timezone]);

    const formatSortDate = (date: string | Dayjs) => {
        const dateTz = dayjs(date);
        return dateTz.format('DD MMM');
    };

    const formatCurrency = useCallback((value: number) => {
        return value.toLocaleString(locale, {
            style: 'currency',
            currency: currency || currencyDefault,
        });
    }, [currency, locale]);

    const formatCurrencySymbol = useCallback((CustomCountry?: string, customCurrency?: string) => {

        return (0).toLocaleString(CustomCountry && locale, {
            style: 'currency',
            currency: customCurrency ? customCurrency : (currency || currencyDefault),
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).replace(/\d/g, '').trim();
    }, [currency, locale]);

    const formatNumber = useCallback((value?: number) => {
        return value?.toLocaleString(locale, {
            style: 'decimal'
        });
    }, [locale]);

    return {
        formatDate,
        formatSortDate,
        formatCurrency,
        formatCurrencySymbol,
        formatNumber,
        formatTime,
        defaultFormat
    };
};